import { useCallback, useEffect, useRef, useState } from "react";

/**
 * A standalone HTML document, mounted in a sandboxed iframe and scaled to fit.
 *
 * The AI worker emits tearsheets as a full HTML document — DOCTYPE, `:root` CSS
 * variables, Google Fonts `<link>`s, paginated `.page` divs sized to US Letter.
 * Rendering that inline would leak its global styles and font imports into the
 * app shell, so it goes in an `<iframe srcDoc>`.
 *
 * The HTML hard-codes `width: 8.5in` on `.page`, which overflows a narrow
 * panel. So measure the document's natural size and the wrapper's available
 * width, then `transform: scale()` to fit — the AI-authored layout at full
 * fidelity in a wide container, shrunk in a narrow one. Scale is capped at 1 so
 * a wide container doesn't stretch the page.
 *
 * Read-only by itself. `onDocumentReady` is where the in-app tearsheet attaches
 * its inline editing; a public lookbook passes nothing and gets a viewer.
 */
export function HtmlDocumentFrame({
  html,
  title,
  onDocumentReady,
  iframeRef: externalIframeRef,
}: {
  html: string;
  title: string;
  /**
   * Called with the loaded iframe document, after the first measurement. Note
   * the document is replaced on every `html` change, so anything attached here
   * is attached again.
   */
  onDocumentReady?: (doc: Document) => void;
  /**
   * The iframe element, for a caller that needs to reach the live document
   * later. Deliberately the element and not the document: React nulls an
   * element ref on unmount, so reading `.contentDocument` through it can't hand
   * a caller a torn-down document to act on.
   */
  iframeRef?: React.RefObject<HTMLIFrameElement>;
}) {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const internalIframeRef = useRef<HTMLIFrameElement>(null);
  const iframeRef = externalIframeRef ?? internalIframeRef;
  const [natural, setNatural] = useState({ width: 864, height: 600 });
  const [containerWidth, setContainerWidth] = useState<number | null>(null);

  const measure = useCallback(() => {
    const doc = iframeRef.current?.contentDocument;
    if (!doc) return;
    const w = Math.max(
      doc.body?.scrollWidth ?? 0,
      doc.documentElement?.scrollWidth ?? 0,
    );
    const h = Math.max(
      doc.body?.scrollHeight ?? 0,
      doc.documentElement?.scrollHeight ?? 0,
    );
    if (w <= 0 || h <= 0) return;
    // Bail out when nothing moved. This measurement runs from a ResizeObserver
    // on the iframe's own document, and the size it writes feeds back into the
    // iframe's width/height — so returning a fresh object every time would keep
    // re-rendering and re-triggering the observer. Returning `prev` lets React
    // skip the render and the cycle settles.
    setNatural((prev) =>
      prev.width === w && prev.height === h ? prev : { width: w, height: h },
    );
    // `iframeRef` is a ref object and never changes identity, but it is derived
    // from a prop rather than created here, so the linter can't see that.
  }, [iframeRef]);

  // Watches the loaded iframe document for late reflow. Held in a ref because
  // it can only be created once the document exists, which is at `load` —
  // before that `contentDocument` is still about:blank.
  const contentObserverRef = useRef<ResizeObserver | null>(null);

  const handleLoad = useCallback(() => {
    measure();
    const doc = iframeRef.current?.contentDocument;
    if (!doc) return;

    onDocumentReady?.(doc);

    // The iframe's measured height is the wrapper's height, and the wrapper
    // clips overflow — so a height taken before the content settles truncates
    // the document, sometimes to a blank first screen. `load` fires before
    // webfonts and images are applied and both reflow the document, so watch
    // for it rather than guessing at a delay: fonts.ready covers the Google
    // Fonts <link>s the worker emits, and the observer covers image decode and
    // anything else.
    doc.fonts?.ready.then(() => measure()).catch(() => {});

    contentObserverRef.current?.disconnect();
    const observer = new ResizeObserver(() => measure());
    if (doc.documentElement) observer.observe(doc.documentElement);
    if (doc.body) observer.observe(doc.body);
    contentObserverRef.current = observer;
  }, [iframeRef, measure, onDocumentReady]);

  // Backstop for browsers that give us neither fonts.ready nor a resize signal.
  useEffect(() => {
    const id = window.setTimeout(measure, 800);
    return () => window.clearTimeout(id);
  }, [html, measure]);

  useEffect(() => {
    return () => {
      contentObserverRef.current?.disconnect();
      contentObserverRef.current = null;
    };
  }, []);

  useEffect(() => {
    const el = wrapperRef.current;
    if (!el) return;
    const observer = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect.width;
      if (w && w > 0) setContainerWidth(w);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // Only pin the iframe to its natural width when we actually need to scale
  // down. If the container is wider than (or equal to) the page's natural
  // width, give the iframe `width: 100%` so the content's own `margin: 0 auto`
  // centers `.page` horizontally — otherwise the iframe stays at 864px and dead
  // space stacks up on the right of a wide panel.
  const needsScaling =
    containerWidth !== null && containerWidth < natural.width;
  const scale = needsScaling ? containerWidth! / natural.width : 1;
  const scaledHeight = needsScaling ? natural.height * scale : natural.height;

  return (
    <div
      ref={wrapperRef}
      style={{
        width: "100%",
        height: scaledHeight,
        overflow: "hidden",
      }}
    >
      {/* The sandbox withholds `allow-scripts`, so nothing in the document
          runs. `allow-same-origin` is what makes contentDocument readable,
          which the measurement above needs; without scripts it grants the
          document no ability to act. `allow-popups` and
          `allow-popups-to-escape-sandbox` keep `target="_blank"` links
          working. */}
      <iframe
        ref={iframeRef}
        srcDoc={html}
        title={title}
        onLoad={handleLoad}
        sandbox="allow-same-origin allow-popups allow-popups-to-escape-sandbox"
        style={{
          width: needsScaling ? natural.width : "100%",
          height: natural.height,
          transform: needsScaling ? `scale(${scale})` : "none",
          transformOrigin: "top left",
          border: "none",
          display: "block",
          backgroundColor: "transparent",
        }}
      />
    </div>
  );
}

/**
 * The HTML a tearsheet version carries, or null when it carries a GenUI schema
 * tree instead.
 *
 * The worker wraps it as `{ html: "<!DOCTYPE…" }` on `ui_data`; a bare string is
 * accepted as a forward-compatibility hedge. Everything the app has stored from
 * before that change is a schema tree, which has no `html` and falls through.
 */
export function htmlPayloadOf(uiData: unknown): string | null {
  if (typeof uiData === "string") return uiData;
  if (
    uiData &&
    typeof uiData === "object" &&
    typeof (uiData as { html?: unknown }).html === "string"
  ) {
    return (uiData as { html: string }).html;
  }
  return null;
}
